


import React, { useState, useEffect } from 'react';

import Parse from 'parse';

import '../styles/SideBarRight.css';
import '../styles/SideBarToggle.css';

import CollapseIconHover from '../../images/IconRightArrowBlue@3x.png';
import CollapseIcon from '../../images/IconRightArrowGrey@3x.png';
import BedIcon from '../../images/IconPlanBed@2x.png';
import BathIcon from '../../images/IconPlanBath@2x.png';
import GarageIcon from '../../images/IconPlanGarage@2x.png';
import PlaceholderImage from '../../images/PlaceholderImage.png';
import IconClear from '../../images/IconClear@3x.png';
import IconClearBlue from '../../images/IconClearBlue@3x.png';


const SideBarRight = ({ isExpanded = true, onRequestClose, selectedPlanId, activeOverlayPlanId, selectedLandInfo, onRequestClearLand, onRequestClearPlan }) => {


  const isVisible = isExpanded;

  // Also add these, which allow us to keep the sidebar content showing until the collapse animation is complete.
  const [isSidebarVisible, setIsSidebarVisible] = useState(isExpanded);
  const [shouldRenderContent, setShouldRenderContent] = useState(isExpanded);

  const [isSideBarCollapseButtonHovering, setIsSideBarCollapseButtonHovering] = useState(false);
  const [isClearLandButtonHovered, setIsClearLandButtonHovered] = useState(false);
  const [isClearPlanButtonHovered, setIsClearPlanButtonHovered] = useState(false);

  const [showSeries, setShowSeries] = useState(true);
  const [showFiles, setShowFiles] = useState(true);
  const [showExamples, setShowExamples] = useState(true);

  const [planData, setPlanData] = useState(null);
  const [seriesName, setSeriesName] = useState('');
  const [planFiles, setPlanFiles] = useState([]);
  const [modifiedPlans, setModifiedPlans] = useState([]);

  const [showPlanInfo, setShowPlanInfo] = useState(true);
  const [showLandInfo, setShowLandInfo] = useState(true);


  /*-----------------------------------------------------------------------------*/


  useEffect(() => {

    const planIdToUse = selectedPlanId || activeOverlayPlanId;

    if (!planIdToUse) {

      setPlanData(null);  // When we are using the clear plan button we also need to do this.
      setSeriesName('');
      setPlanFiles([]);
      setModifiedPlans([]);

      return;
    }

    // Fetch plan details when selectedPlanId changes.
    const fetchPlanDetails = async () => {
      const Plan = Parse.Object.extend('Plan');
      const query = new Parse.Query(Plan);
      query.include('series');

      let plan;
      try {
        plan = await query.get(planIdToUse);
      } catch (err) {
        // Treat missing plan (e.g. AccessoryOverlay) as "no plan selected"
        setPlanData(null);
        setSeriesName('');
        setPlanFiles([]);
        setModifiedPlans([]);
        return;
      }

      const cleanPlan = {
        id: plan.id,
        name: plan.get('name'),
        size: plan.get('size'),
        bedrooms: plan.get('bedrooms'),
        bathrooms: plan.get('bathrooms'),
        garageSpaces: plan.get('garageSpaces'),
        renderThumb: plan.get('renderThumb')?.url(),
      };
      setPlanData(cleanPlan);

      // Also retreive the series name.
      const series = plan.get('series');
      setSeriesName(series?.get('name') || '');

      const FileType = Parse.Object.extend('FileType');
      const fileTypeQuery = new Parse.Query(FileType);
      fileTypeQuery.equalTo('name', 'Plans');
      const fileType = await fileTypeQuery.first();

      // Fetch the files (drawings) associated with the plan.
      const PlanFile = Parse.Object.extend('PlanFile');
      const fileQuery = new Parse.Query(PlanFile);
      fileQuery.equalTo('plan', plan);
      fileQuery.equalTo('fileType', fileType);
      fileQuery.include('file');
      const results = await fileQuery.find();

      const cleanedFiles = results.map(f => ({
        name: fileType.get('name'),  // will be "Plans" as that is all we are querying at the moment.
        url: f.get('file')?.url(),
      }));

      setPlanFiles(cleanedFiles);

      // Fetch modified plans associated with the selected plan.
      const ModifiedPlan = Parse.Object.extend('ModifiedPlan');
      const modQuery = new Parse.Query(ModifiedPlan);
      modQuery.equalTo('parentPlan', plan);
      modQuery.include('modifiedPlan');
      const modResults = await modQuery.find();

      const cleanedMods = modResults.map(mp => ({
        description: mp.get('modDescription'),
        url: mp.get('modifiedPlan')?.url(),
      }));

      setModifiedPlans(cleanedMods);

    };

    fetchPlanDetails();
  }, [selectedPlanId, activeOverlayPlanId]);


  useEffect(() => {

    if (isExpanded) {
      setShouldRenderContent(true);
      setIsSidebarVisible(true);
      setIsSideBarCollapseButtonHovering(false);  // Reset the button state.
    }
    else {
      setIsSidebarVisible(false); // triggers collapse
      setTimeout(() => {
        setShouldRenderContent(false);
      }, 300); // match your CSS transition duration
    }

  }, [isExpanded]);


  // Extract the land area from the estate_description in the LINZ returned values.
  const extractLandArea = (description) => {

    if (!description) return null;

    const parts = description.trim().split(' ');
    const last = parts[parts.length - 1];
    const secondLast = parts[parts.length - 2];

    if (last === 'm2' && secondLast) {
      const numeric = parseInt(secondLast.replace(/,/g, ''), 10);
      return isNaN(numeric) ? null : numeric;
    }

    return null;
  };


  const area =
    selectedLandInfo && selectedLandInfo.estate_description
      ? extractLandArea(selectedLandInfo.estate_description)
      : null;


  let siteCoverage = null;
  if (planData?.size && area && area > 0) {
    const coverage = (planData.size / area) * 100;

    if (coverage > 0 && coverage < 1) {
      siteCoverage = '<1% site coverage';
    } else {
      siteCoverage = `${Math.round(coverage)}% site coverage`;
    }
  }


  const handleClearLand = () => {
    if (typeof window.clearLandOverlay === 'function') {
      window.clearLandOverlay(); // assumes your map code exposes this globally
    }
    // Clear land info in the sidebar
    onRequestClearLand?.(); // Call a parent handler if passed
  };


  // Add a useEffect() in SideBarRight.js to reset the hover state after the land
  // is cleared (otherwise it hangs around).
  useEffect(() => {
    setIsClearLandButtonHovered(null);
  }, [selectedLandInfo]);


  // We also need to reset the hover state when we clear the plan as it is in hover
  // mode when we add our next plan back.
  useEffect(() => {
    setIsClearPlanButtonHovered(false);
  }, [selectedPlanId]);


  /*-----------------------------------------------------------------------------*/


  return (

    // Outer div - include code for animation.
    <div
      className="sidebarRight"
      style={{
        width: '100%',
        flex: 1,
        opacity: isSidebarVisible ? 1 : 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: isVisible ? '0.75px solid #596A99' : 'none',
        transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
        position: 'relative',
      }}
    >

      {/* Minimise toggle */}
      {isVisible && (
        <button
          className="sidebar-toggle-button sidebar-right-toggle-button-expanded"
          onClick={onRequestClose}
          onMouseEnter={() => setIsSideBarCollapseButtonHovering(true)}
          onMouseLeave={() => setIsSideBarCollapseButtonHovering(false)}
        >
          <img
            src={isSideBarCollapseButtonHovering ? CollapseIconHover : CollapseIcon}
            alt="Hide Sidebar"
          />
        </button>
      )}

      {/* Main Content */}
      {shouldRenderContent && (
        <div className="sidebarRight-inner">
          <div className="sidebarRight-scrollable">
            <div className="sidebarRight-scrollarea">
              {planData && (
                <div className="sidebarRight-plan">
                  <img
                    src={planData.renderThumb || PlaceholderImage}
                    alt="Plan preview"
                    className="sidebarRight-plan-image"
                  />

                  <div className="sidebarRight-plan-icons-container">
                    <div className="sidebarRight-plan-icons">
                      <div className="sidebarRight-plan-icon-group">
                        <img src={BedIcon} alt="Bedrooms" />
                        <span>{planData.bedrooms}</span>
                      </div>
                      <div className="sidebarRight-plan-icon-group">
                        <img src={BathIcon} alt="Bathrooms" />
                        <span>{planData.bathrooms}</span>
                      </div>
                      <div className="sidebarRight-plan-icon-group">
                        <img src={GarageIcon} alt="Garage" />
                        <span>{planData.garageSpaces}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="sidebarRight-plan-info-title"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div
                      style={{ flexGrow: 1, cursor: 'pointer' }}
                      onClick={() => setShowPlanInfo(!showPlanInfo)}
                    >
                      {planData.name || 'Unnamed Plan'}
                      <span className="sidebarRight-collapse-arrow">
                        {showPlanInfo ? '▾' : '▸'}
                      </span>
                    </div>

                    <img
                      src={isClearPlanButtonHovered ? IconClearBlue : IconClear}
                      alt="Clear Plan Info"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestClearPlan?.();
                      }}
                      onMouseEnter={() => setIsClearPlanButtonHovered(true)}
                      onMouseLeave={() => setIsClearPlanButtonHovered(false)}
                      style={{
                        height: '18px',
                        width: '18px',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        transition: 'box-shadow 0.2s ease',
                        boxShadow: isClearPlanButtonHovered
                          ? 'var(--ph-box-shadow-hover)'
                          : 'none'
                      }}
                    />
                  </div>

                  <div className={`sidebarRight-collapsible-content ${showPlanInfo ? 'expanded' : 'collapsed'}`}>

                    <div className="sidebarRight-plan-info-size">
                      <span>{planData.size.toLocaleString()} m²</span>
                      {siteCoverage && (
                        <span className="sidebarRight-plan-info-coverage">{siteCoverage}</span>
                      )}
                    </div>

                    {seriesName && (
                      <div className="sidebarRight-planfiles-section">
                        <div
                          className="sidebarRight-collapsible-label"
                          onClick={() => setShowSeries(!showSeries)}
                        >
                          Range
                          <span className="sidebarRight-collapse-arrow">{showSeries ? '▾' : '▸'}</span>
                        </div>
                        <div
                          className={`sidebarRight-collapsible-content ${showSeries ? 'expanded' : 'collapsed'}`}
                        >
                          <div className="sidebarRight-planfiles-value">{seriesName}</div>
                        </div>
                      </div>
                    )}

                    {planFiles.length > 0 && (
                      <div className="sidebarRight-planfiles-section">
                        <div
                          className="sidebarRight-collapsible-label"
                          onClick={() => setShowFiles(!showFiles)}
                        >
                          Files <span className="sidebarRight-collapse-arrow">{showFiles ? '▾' : '▸'}</span>
                        </div>
                        <div
                          className={`sidebarRight-collapsible-content ${showFiles ? 'expanded' : 'collapsed'}`}
                        >
                          {planFiles.map((f, i) => (
                            <a
                              key={i}
                              className="sidebarRight-planfiles-link"
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {f.name || 'Open File'}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {modifiedPlans.length > 0 && (
                      <div className="sidebarRight-planfiles-section">
                        <div
                          className="sidebarRight-planfiles-label sidebarRight-collapsible-label"
                          onClick={() => setShowExamples(!showExamples)}
                        >
                          PERSONALISED EXAMPLES
                          <span className="sidebarRight-collapse-arrow">{showExamples ? '▾' : '▸'}</span>
                        </div>

                        <div
                          className={`sidebarRight-collapsible-content ${showExamples ? 'expanded' : 'collapsed'}`}
                        >
                          {modifiedPlans.map((mod, i) => (
                            <a
                              key={i}
                              className="sidebarRight-planfiles-link"
                              href={mod.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {mod.description || 'View Example'}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>

                </div>
              )}

              {selectedLandInfo && (

                <div
                  className="sidebarRight-planfiles-section"
                  style={{ marginTop: !selectedPlanId ? '25px' : '15px' }}
                >

                  <div
                    className="sidebarRight-land-info-title"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div
                      style={{ flexGrow: 1 }}
                      onClick={() => setShowLandInfo(!showLandInfo)}
                    >
                      Land Information
                      <span className="sidebarRight-collapse-arrow">
                        {showLandInfo ? '▾' : '▸'}
                      </span>
                    </div>

                    <img
                      src={isClearLandButtonHovered ? IconClearBlue : IconClear}
                      alt="Clear Land Info"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent toggling open/closed when clicking the icon
                        handleClearLand();
                      }}
                      style={{
                        height: '18px',
                        width: '18px',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        boxShadow: isClearLandButtonHovered
                          ? 'var(--ph-box-shadow-hover)'
                          : 'none',
                        transition: 'box-shadow 0.2s ease'
                      }}
                      onMouseEnter={() => setIsClearLandButtonHovered(true)}
                      onMouseLeave={() => setIsClearLandButtonHovered(false)}
                    />
                  </div>

                  <div className={`sidebarRight-collapsible-content ${showLandInfo ? 'expanded' : 'collapsed'}`}>

                    <div className="sidebarRight-landinfo-section">

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">Estate Description:</span>
                        <span className="value">{selectedLandInfo.estate_description}</span>
                      </div>

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">Title No:</span>
                        <span className="value">{selectedLandInfo.title_no}</span>
                      </div>

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">Title Issued:</span>
                        <span className="value">{selectedLandInfo.issue_date}</span>
                      </div>

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">Type:</span>
                        <span className="value">{selectedLandInfo.type}</span>
                      </div>

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">District:</span>
                        <span className="value">{selectedLandInfo.land_district}</span>
                      </div>

                      <div className="sidebarRight-landinfo-row">
                        <span className="label">Owners:</span>
                        <span className="value">{selectedLandInfo.number_owners}</span>
                      </div>

                      {area && (
                        <div className="sidebarRight-landinfo-row">
                          <span className="label">Site Area:</span>
                          <span className="value">{area.toLocaleString()} m²</span>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              )}

              {/* Onboarding instructions - only show if there is no plan or site selected. */}
              {!planData && !selectedLandInfo && (
                <div className="sidebarRight-empty">
                  <div className="sidebarRight-onboarding">

                    <div className="sidebarRight-onboarding-step">
                      <div className="sidebarRight-onboarding-step-header">Step 1:</div>
                      <div className="sidebarRight-onboarding-step-text">
                        <p>Use the search button to locate your site on the map.</p>
                      </div>
                    </div>

                    <div className="sidebarRight-onboarding-step">
                      <div className="sidebarRight-onboarding-step-header">Step 2:</div>
                      <div className="sidebarRight-onboarding-step-text">
                        <p>On the map, click/tap and hold somewhere within the section area to highlight the boundaries and download the parcel data from LINZ.</p>
                        <p>Try the different map layers in the left side bar – the best one to use depends on your location.</p>
                      </div>
                    </div>

                    <div className="sidebarRight-onboarding-step">
                      <div className="sidebarRight-onboarding-step-header">Step 3:</div>
                      <div className="sidebarRight-onboarding-step-text">
                        <p>Choose the plans from the left side bar to trial on your site.</p>
                        <p>Drag the plan to the correct location, and use the slider (or shift+drag across the plan) to orient it correctly.</p>
                        <p>Mirror the plan if needed using the button.</p>
                      </div>
                    </div>

                    <div className="sidebarRight-onboarding-step">
                      <div className="sidebarRight-onboarding-step-text"><strong>Experiment away!</strong></div>
                    </div>

                  </div>
                </div>
              )}

              <div className="sidebarRight-version">v1.0.4</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default SideBarRight;



